import {
  DropdownButtonType,
  SingleSelectDropdown,
} from '@tableau/widgets';
import React from 'react';

import { Location } from '~common/react/Location';
import {
  FavoritesMenuNavigateToFavoriteEvent,
  FavoritesMenuOpenEvent,
  TelemetryEvents,
} from '~common/telemetryEvents/TelemetryEvents';
import { imgPath } from '~common/utilities/ImgUrl';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { Messages } from '~src/codegen/Localize';
import { favoritesSpaceDefaultSortOrder } from '~src/favorites/models/FavoritesSortOptions';
import { Favorites } from '~src/favorites/resources/Favorites';
import { PageHeaderStyles } from '~src/spacesPlatform/components/layout/PageHeaderStyles';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { IFavorite } from '~src/ts/ServerApi/types/IFavorite';

import { FavoritesList, getFavoriteKey } from './FavoritesList';
import cssVar = PageHeaderStyles.CssVariables.cssVar;
import CssVariables = PageHeaderStyles.CssVariables;

// we just want to grab all favorites, we usually use 1000 as max number of items to fetch
const PAGESIZE_MAX_SLICE: number = 1000;
const favoritesMenuPopupId = 'favorites-menu-popup';

export interface FavoritesDropdownProps {
  readonly siteUrls: SpacesSiteUrls;
}

export interface FavoritesDropdownState {
  readonly favorites: IFavorite[];
  readonly favoritesRequestComplete: boolean;
  readonly buttonHover: boolean;
  readonly panelOpened: boolean;
}

export class FavoritesDropdown extends React.Component<FavoritesDropdownProps, FavoritesDropdownState> {
  private unmounted: boolean = false;

  public constructor(props: FavoritesDropdownProps) {
    super(props);
    this.state = {favorites: [], favoritesRequestComplete: false, buttonHover: false, panelOpened: false};
  }

  public componentWillUnmount() {
    this.unmounted = false;
  }

  public render(): JSX.Element {
    return (
      <div className={'tb-favorites-dropdown'} style={DropdownContainerStyle}>
        <div onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
          <SingleSelectDropdown
            children={this.getButtonContent()}
            type={DropdownButtonType.Text}
            list={this.getList()}
            value={''}
            testId="favorites-menu"
            onOpen={this.handleOpen}
            onClose={this.handleClose}
            onChange={this.handleChange}
            label={''}
            aria-controls={favoritesMenuPopupId}
            aria-label={Messages.favoritesMenu()}
            title={this.closePanel ? Messages.favoritesMenuHide() : Messages.favoritesMenuShow()}
          />
          </div>
      </div>
    );
  }

  private getList = () => {
    return (
      <FavoritesList
        id={favoritesMenuPopupId}
        onSelect={this.handleChange}
        urlForFavorite={this.urlForFavorite}
        items={this.state.favorites}
        favoritesRequestCompleted={this.state.favoritesRequestComplete} />
    );
  }

  private closePanel: () => void;

  private handleOpen = (done: () => void): void => {
    // save close panel for later
    this.closePanel = done;
    window.addEventListener('blur', this.onWindowBlur);

    const favoritesFetcher = FeatureFlags.useCollectionsFavorites
      ? Favorites.fetchFromExploreFavoriteContentForUser()
      : Favorites.fetchFromGetFavorites();

    favoritesFetcher
      .withOrder(favoritesSpaceDefaultSortOrder)
      .getSlice(0, PAGESIZE_MAX_SLICE)
      .then((result) => {
        Telemetry.sendEvent<FavoritesMenuOpenEvent>({
          type: TelemetryEvents.FavoritesMenuOpen,
          favoriteItemCount: result.total,
        });
        this.updateState({favorites: result.items, favoritesRequestComplete: true, panelOpened: true});
      }).catch(() => {
        this.updateState({favoritesRequestComplete: true, panelOpened: true});
      });
  }

  private handleClose = (): void => {
    if (this.closePanel) {
      this.closePanel();
      this.closePanel = null;
      this.updateState({panelOpened: false});
    }
  }

  private onWindowBlur = (): void => {
    this.handleClose();
    window.removeEventListener('blur', this.onWindowBlur);
  }

  private urlForFavorite = (favorite: IFavorite): string => {
    let redirectUrl = '';
    switch(favorite.objectType) {
      case 'datasource':
        if (favorite.objectId === '') {
          redirectUrl = this.props.siteUrls.favoriteDatasourceDoesntExistUrl(favorite.objectIdForFavoriting);
        } else  {
          redirectUrl = this.props.siteUrls.favoriteDatasourceUrl(favorite.objectId, favorite.objectIdForFavoriting);
        }
        break;

      case 'project':
        redirectUrl = this.props.siteUrls.favoriteProjectUrl(favorite.objectId);
        break;

      case 'workbook':
        redirectUrl = this.props.siteUrls.favoriteWorkbookUrl(favorite.objectId);
        break;

      case 'view':
        redirectUrl = this.props.siteUrls.redirectToFavoriteViewUrl(favorite.objectId);
        break;

      case 'flow':
        redirectUrl = this.props.siteUrls.favoriteFlowUrl(favorite.objectId);
        break;

      case 'dataRole':
        redirectUrl = this.props.siteUrls.redirectToDataRoleUrl(favorite.objectId);
        break;

      case 'lens':
        redirectUrl = this.props.siteUrls.lensUrl(favorite.objectId);
        break;

      case 'metric':
        redirectUrl = this.props.siteUrls.favoriteMetricUrl(favorite.objectId);
        break;

      case 'virtualConnection':
        redirectUrl = this.props.siteUrls.favoritePublishedConnectionUrl(favorite.objectId);
        break;

      case 'virtualConnectionTable':
        redirectUrl = this.props.siteUrls.favoritePublishedConnectionTableUrl(favorite.objectId);
        break;

      case 'collection':
        redirectUrl = this.props.siteUrls.collectionUrl(favorite.objectLuid);
        break;
    }

    return redirectUrl;
  }

  private handleChange = (value: string) => {
    const favorite = this.state.favorites.find((favorite) => {
      return getFavoriteKey(favorite) === value;
    });

    if (!favorite) {
      return;
    }

    const redirectUrl = this.urlForFavorite(favorite);
    if (redirectUrl) {
      Telemetry.sendEvent<FavoritesMenuNavigateToFavoriteEvent>({
        type: TelemetryEvents.FavoritesMenuNavigateToFavorite,
        favoriteItemType: favorite.objectType,
        favoriteItemId: favorite.objectIdForFavoriting, // TODO: once unified data sources have been sorted out, change this to objectId
      });
      Location.instance.setUrl(redirectUrl);
    }

    this.handleClose();
  }

  private onMouseEnter = () => {
    this.updateState({buttonHover: true});
  }

  private onMouseLeave = () => {
    this.updateState({buttonHover: false});
  }

  private getButtonContent = () => {
    const active = this.state.panelOpened || this.state.buttonHover;
    const themeSuffix = FeatureFlags.vizToolbarRedesignEnabled ? '_dark' : '';

    const imgSrc = active
      ? imgPath(`spaces_toolbar_favorite_active${themeSuffix}.svg`)
      : imgPath(`spaces_toolbar_favorite${themeSuffix}.svg`);

    const imageSize = FeatureFlags.vizToolbarRedesignEnabled ? 18 : 24;

    let backgroundColor = 'unset';
    if (FeatureFlags.vizToolbarRedesignEnabled && this.state.buttonHover) {
      backgroundColor = cssVar(CssVariables.headerBackgroundHoverColor);
    }

    return(
      <div style={{backgroundColor, border: 'none', cursor: 'pointer', width: 32, height: 32, display: 'flex', justifyContent: 'center', alignItems: 'center'}} data-tb-test-id={'favorites-dropdown-Button'}>
        <img
          style={{display:'block'}}
          src={imgSrc}
          width={imageSize}
          height={imageSize}
          alt={''}
        />
      </div>
    );
  }

  private updateState<K extends keyof FavoritesDropdownState>(newState: Pick<FavoritesDropdownState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

const DropdownContainerStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
};

export const PageHeaderFavoritesDropdown: React.ComponentType<FavoritesDropdownProps> =
  FavoritesDropdown;
